/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Risk Algorithm - Sistema de Análisis de Riesgo WhatsApp
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file riskAlgorithm.ts
 * @version 1.0.0
 * @description Algoritmo completo de cálculo de riesgo basado en 10 factores
 * 
 * ORIGEN: FileUploadProcessor.tsx (líneas 500-900) - Copiado EXACTO + optimizado
 * 
 * FACTORES EVALUADOS (pesos):
 * 1. Mensajes por hora (15%) - ≤50 óptimo
 * 2. Tamaño de lista (12%) - ≤200 óptimo
 * 3. Longitud del mensaje (18%) - ≤250 chars óptimo
 * 4. Emojis (15%) - ≤4 óptimo
 * 5. Enlaces (22%) - 0 óptimo, -25% por enlace
 * 6. Variables dinámicas (12%) - ≥1 óptimo
 * 7. Horarios de operación (10%) - 09:00-18:00 óptimo
 * 8. Días de envío (12%) - Lun-Vie óptimo
 * 9. Primera Respuesta IA (8%) - Habilitada óptimo
 * 10. Estado WhatsApp (10%) - .env completo óptimo
 */

import { WhatsAppRiskAssessment, WhatsAppRiskFactor } from '../types/bulksend.types';

// ═══════════════════════════════════════════════════════════════════════════
// TIPOS INTERNOS
// ═══════════════════════════════════════════════════════════════════════════

interface RiskCalculationInput {
  // Factor 1: Mensajes por hora
  totalContacts: number;
  estimatedHours?: number;
  
  // Factor 2: Tamaño de lista
  listSize: number;
  
  // Factor 3: Longitud del mensaje
  messageLength: number;
  
  // Factor 4: Emojis
  emojiCount: number;
  
  // Factor 5: Enlaces
  linkCount: number;
  
  // Factor 6: Variables dinámicas
  variableCount: number;
  hasPersonalization: boolean;
  
  // Factor 7: Horarios
  scheduledHour?: number; // 0-23
  
  // Factor 8: Días
  scheduledDay?: string; // 'MON', 'TUE', etc.
  sendingDays?: string[]; // ['MON', 'TUE', 'WED']
  
  // Factor 9: Primera Respuesta IA
  firstResponseEnabled: boolean;
  
  // Factor 10: Estado WhatsApp
  envConfigured: boolean;
  scheduleConfigured: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES - PESOS Y RANGOS ÓPTIMOS
// ═══════════════════════════════════════════════════════════════════════════

const FACTOR_WEIGHTS = {
  MESSAGES_PER_HOUR: 0.15,
  LIST_SIZE: 0.12,
  MESSAGE_LENGTH: 0.18,
  EMOJIS: 0.15,
  LINKS: 0.22,
  VARIABLES: 0.12,
  SCHEDULE_HOURS: 0.10,
  SENDING_DAYS: 0.12,
  FIRST_RESPONSE: 0.08,
  WHATSAPP_STATUS: 0.10
};

const OPTIMAL_RANGES = {
  MESSAGES_PER_HOUR: { min: 0, max: 50, ideal: 30 },
  LIST_SIZE: { min: 0, max: 200, ideal: 100 },
  MESSAGE_LENGTH: { min: 50, max: 250, ideal: 150 },
  EMOJIS: { min: 0, max: 4, ideal: 2 },
  LINKS: { min: 0, max: 0, ideal: 0 },
  VARIABLES: { min: 1, max: 10, ideal: 3 },
  SCHEDULE_START: 9,
  SCHEDULE_END: 18,
  WEEKEND_DAYS: ['SAT', 'SUN']
};

// ═══════════════════════════════════════════════════════════════════════════
// FUNCIÓN PRINCIPAL - CALCULAR RIESGO COMPLETO
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Calcula el assessment completo de riesgo
 * 
 * @param input - Datos de entrada para el cálculo
 * @returns WhatsAppRiskAssessment con score, nivel, factores y recomendaciones
 */
export function calculateRiskAssessment(input: RiskCalculationInput): WhatsAppRiskAssessment {
  // Calcular cada factor individual
  const factors: WhatsAppRiskFactor[] = [
    evaluateFactor1_MessagesPerHour(input),
    evaluateFactor2_ListSize(input),
    evaluateFactor3_MessageLength(input),
    evaluateFactor4_Emojis(input),
    evaluateFactor5_Links(input),
    evaluateFactor6_Variables(input),
    evaluateFactor7_ScheduleHours(input),
    evaluateFactor8_SendingDays(input),
    evaluateFactor9_FirstResponse(input),
    evaluateFactor10_WhatsAppStatus(input)
  ];
  
  // Calcular score total (promedio ponderado)
  const overallScore = calculateWeightedScore(factors);
  
  // Determinar nivel de riesgo
  const riskLevel = determineRiskLevel(overallScore);
  
  // Calcular probabilidad de flag
  const flagProbability = calculateFlagProbability(overallScore, factors);
  
  // Generar recomendaciones priorizadas
  const recommendations = generateRecommendations(factors, input);
  
  return {
    overallScore: Math.round(overallScore),
    riskLevel,
    factors,
    recommendations,
    flagProbability
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// EVALUACIÓN DE FACTORES INDIVIDUALES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Factor 1: Mensajes por hora (peso 15%)
 * Óptimo: ≤50 msg/hora
 * Warning: 51-75 msg/hora
 * Crítico: >75 msg/hora
 */
function evaluateFactor1_MessagesPerHour(input: RiskCalculationInput): WhatsAppRiskFactor {
  const { totalContacts, estimatedHours = 0 } = input;
  
  let messagesPerHour = 0;
  if (estimatedHours > 0) {
    messagesPerHour = totalContacts / estimatedHours;
  } else {
    // Asumir rate limit de 50/hora si no hay estimate
    messagesPerHour = Math.min(totalContacts, 50);
  }
  
  let score = 100;
  let status: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
  let recommendation: string | undefined;
  
  if (messagesPerHour <= 50) {
    score = 100;
    status = 'excellent';
  } else if (messagesPerHour <= 75) {
    score = 60;
    status = 'warning';
    recommendation = `Reducir velocidad a ≤50 msg/hora. Actual: ${Math.round(messagesPerHour)} msg/hora`;
  } else {
    score = 20;
    status = 'danger';
    recommendation = `¡CRÍTICO! Reducir urgentemente a ≤50 msg/hora. Actual: ${Math.round(messagesPerHour)} msg/hora`;
  }
  
  return {
    id: 'messages_per_hour',
    name: 'Mensajes por Hora',
    description: 'Velocidad de envío de mensajes',
    weight: FACTOR_WEIGHTS.MESSAGES_PER_HOUR,
    currentValue: Math.round(messagesPerHour),
    optimalRange: OPTIMAL_RANGES.MESSAGES_PER_HOUR,
    score,
    status,
    recommendation
  };
}

/**
 * Factor 2: Tamaño de lista (peso 12%)
 * Óptimo: ≤200 contactos
 * Warning: 201-300 contactos
 * Crítico: >300 contactos
 */
function evaluateFactor2_ListSize(input: RiskCalculationInput): WhatsAppRiskFactor {
  const { listSize } = input;
  
  let score = 100;
  let status: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
  let recommendation: string | undefined;
  
  if (listSize <= 200) {
    score = 100;
    status = 'excellent';
  } else if (listSize <= 300) {
    score = 65;
    status = 'warning';
    recommendation = `Considerar dividir lista. Actual: ${listSize} contactos`;
  } else {
    score = 30;
    status = 'danger';
    recommendation = `¡Dividir lista urgentemente! Actual: ${listSize} contactos, óptimo: ≤200`;
  }
  
  return {
    id: 'list_size',
    name: 'Tamaño de Lista',
    description: 'Cantidad total de contactos',
    weight: FACTOR_WEIGHTS.LIST_SIZE,
    currentValue: listSize,
    optimalRange: OPTIMAL_RANGES.LIST_SIZE,
    score,
    status,
    recommendation
  };
}

/**
 * Factor 3: Longitud del mensaje (peso 18%)
 * Óptimo: ≤250 caracteres
 * Medio: 251-350 caracteres
 * Alto: 351-550 caracteres
 * Crítico: >550 caracteres
 */
function evaluateFactor3_MessageLength(input: RiskCalculationInput): WhatsAppRiskFactor {
  const { messageLength } = input;
  
  let score = 100;
  let status: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
  let recommendation: string | undefined;
  
  if (messageLength <= 250) {
    score = 100;
    status = 'excellent';
  } else if (messageLength <= 350) {
    score = 50;
    status = 'good';
    recommendation = `Reducir mensaje a ≤250 chars. Actual: ${messageLength} chars`;
  } else if (messageLength <= 550) {
    score = 30;
    status = 'warning';
    recommendation = `Mensaje muy largo. Actual: ${messageLength} chars, óptimo: ≤250`;
  } else {
    score = 10;
    status = 'danger';
    recommendation = `¡CRÍTICO! Mensaje demasiado largo: ${messageLength} chars. Reducir a ≤250`;
  }
  
  return {
    id: 'message_length',
    name: 'Longitud del Mensaje',
    description: 'Cantidad de caracteres del mensaje',
    weight: FACTOR_WEIGHTS.MESSAGE_LENGTH,
    currentValue: messageLength,
    optimalRange: OPTIMAL_RANGES.MESSAGE_LENGTH,
    score,
    status,
    recommendation
  };
}

/**
 * Factor 4: Emojis (peso 15%)
 * Óptimo: ≤4 emojis
 * Warning: 5-6 emojis
 * Crítico: >6 emojis
 */
function evaluateFactor4_Emojis(input: RiskCalculationInput): WhatsAppRiskFactor {
  const { emojiCount } = input;
  
  let score = 100;
  let status: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
  let recommendation: string | undefined;
  
  if (emojiCount <= 4) {
    score = 100;
    status = 'excellent';
  } else if (emojiCount <= 6) {
    score = 50;
    status = 'warning';
    recommendation = `Reducir emojis a ≤4. Actual: ${emojiCount} emojis`;
  } else {
    score = 20;
    status = 'danger';
    recommendation = `¡Demasiados emojis! Actual: ${emojiCount}, óptimo: ≤4`;
  }
  
  return {
    id: 'emojis',
    name: 'Emojis',
    description: 'Cantidad de emojis en el mensaje',
    weight: FACTOR_WEIGHTS.EMOJIS,
    currentValue: emojiCount,
    optimalRange: OPTIMAL_RANGES.EMOJIS,
    score,
    status,
    recommendation
  };
}

/**
 * Factor 5: Enlaces (peso 22%) - EL MÁS IMPORTANTE
 * Óptimo: 0 enlaces
 * Warning: 1 enlace (-25%)
 * Alto: 2 enlaces (-50%)
 * Crítico: ≥3 enlaces (-75%)
 */
function evaluateFactor5_Links(input: RiskCalculationInput): WhatsAppRiskFactor {
  const { linkCount } = input;
  
  let score = 100;
  let status: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
  let recommendation: string | undefined;
  
  if (linkCount === 0) {
    score = 100;
    status = 'excellent';
  } else if (linkCount === 1) {
    score = 75;
    status = 'warning';
    recommendation = 'Eliminar enlace para mejorar score (+25 puntos)';
  } else if (linkCount === 2) {
    score = 50;
    status = 'warning';
    recommendation = 'Eliminar enlaces (2 enlaces detectados, penalización -50%)';
  } else {
    score = 25;
    status = 'danger';
    recommendation = `¡CRÍTICO! ${linkCount} enlaces detectados. Eliminar todos (+75 puntos)`;
  }
  
  return {
    id: 'links',
    name: 'Enlaces',
    description: 'Cantidad de URLs en el mensaje',
    weight: FACTOR_WEIGHTS.LINKS,
    currentValue: linkCount,
    optimalRange: OPTIMAL_RANGES.LINKS,
    score,
    status,
    recommendation
  };
}

/**
 * Factor 6: Variables dinámicas (peso 12%)
 * Óptimo: ≥1 variable (mensaje personalizado)
 * Warning: Sin variables (mensaje genérico)
 */
function evaluateFactor6_Variables(input: RiskCalculationInput): WhatsAppRiskFactor {
  const { variableCount, hasPersonalization } = input;
  
  let score = 100;
  let status: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
  let recommendation: string | undefined;
  
  if (variableCount >= 1 || hasPersonalization) {
    score = 100;
    status = 'excellent';
  } else {
    score = 40;
    status = 'warning';
    recommendation = 'Agregar variables personalizadas ([nombre], [curso], etc.) para mejorar score';
  }
  
  return {
    id: 'variables',
    name: 'Variables Dinámicas',
    description: 'Personalización del mensaje',
    weight: FACTOR_WEIGHTS.VARIABLES,
    currentValue: variableCount,
    optimalRange: OPTIMAL_RANGES.VARIABLES,
    score,
    status,
    recommendation
  };
}

/**
 * Factor 7: Horarios de operación (peso 10%)
 * Óptimo: 09:00-18:00
 * Warning: 08:00-20:00
 * Crítico: Fuera de horario laboral
 */
function evaluateFactor7_ScheduleHours(input: RiskCalculationInput): WhatsAppRiskFactor {
  const { scheduledHour } = input;
  
  let score = 100;
  let status: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
  let recommendation: string | undefined;
  
  if (scheduledHour === undefined) {
    // No programado, asumir óptimo
    score = 100;
    status = 'excellent';
  } else if (scheduledHour >= 9 && scheduledHour < 18) {
    score = 100;
    status = 'excellent';
  } else if (scheduledHour >= 8 && scheduledHour < 20) {
    score = 60;
    status = 'warning';
    recommendation = 'Programar entre 09:00-18:00 para mejor resultado';
  } else {
    score = 20;
    status = 'danger';
    recommendation = '¡CRÍTICO! Horario fuera de rango laboral. Programar entre 09:00-18:00';
  }
  
  return {
    id: 'schedule_hours',
    name: 'Horarios de Operación',
    description: 'Hora programada de envío',
    weight: FACTOR_WEIGHTS.SCHEDULE_HOURS,
    currentValue: scheduledHour !== undefined ? `${scheduledHour}:00` : 'No programado',
    optimalRange: { start: 9, end: 18 },
    score,
    status,
    recommendation
  };
}

/**
 * Factor 8: Días de envío (peso 12%)
 * Óptimo: 1-3 días hábiles
 * Medio: 4-5 días hábiles
 * Alto: Incluye fines de semana (85% riesgo)
 */
function evaluateFactor8_SendingDays(input: RiskCalculationInput): WhatsAppRiskFactor {
  const { scheduledDay, sendingDays = [] } = input;
  
  let score = 100;
  let status: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
  let recommendation: string | undefined;
  
  // Detectar si incluye fines de semana
  const hasWeekend = sendingDays.some(day => 
    OPTIMAL_RANGES.WEEKEND_DAYS.includes(day.toUpperCase())
  );
  
  if (scheduledDay && OPTIMAL_RANGES.WEEKEND_DAYS.includes(scheduledDay.toUpperCase())) {
    score = 15;
    status = 'danger';
    recommendation = '¡CRÍTICO! No enviar en fines de semana (85% riesgo). Programar Lun-Vie';
  } else if (hasWeekend) {
    score = 15;
    status = 'danger';
    recommendation = '¡CRÍTICO! Configuración incluye fines de semana. Cambiar a Lun-Vie';
  } else if (sendingDays.length >= 4) {
    score = 50;
    status = 'good';
    recommendation = 'Considerar reducir a 1-3 días hábiles';
  } else {
    score = 100;
    status = 'excellent';
  }
  
  return {
    id: 'sending_days',
    name: 'Días de Envío',
    description: 'Días configurados para envío',
    weight: FACTOR_WEIGHTS.SENDING_DAYS,
    currentValue: scheduledDay || sendingDays.join(', ') || 'No configurado',
    optimalRange: { optimal: 'Lun-Vie', avoid: 'Sáb-Dom' },
    score,
    status,
    recommendation
  };
}

/**
 * Factor 9: Primera Respuesta IA (peso 8%)
 * Óptimo: Habilitada
 * Warning: Deshabilitada
 */
function evaluateFactor9_FirstResponse(input: RiskCalculationInput): WhatsAppRiskFactor {
  const { firstResponseEnabled } = input;
  
  let score = 100;
  let status: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
  let recommendation: string | undefined;
  
  if (firstResponseEnabled) {
    score = 100;
    status = 'excellent';
  } else {
    score = 50;
    status = 'warning';
    recommendation = 'Habilitar Primera Respuesta IA en Settings para mejorar engagement';
  }
  
  return {
    id: 'first_response',
    name: 'Primera Respuesta IA',
    description: 'Respuesta automática inteligente',
    weight: FACTOR_WEIGHTS.FIRST_RESPONSE,
    currentValue: firstResponseEnabled ? 'Habilitada' : 'Deshabilitada',
    optimalRange: { optimal: 'Habilitada' },
    score,
    status,
    recommendation
  };
}

/**
 * Factor 10: Estado WhatsApp (peso 10%)
 * Óptimo: .env completo y válido
 * Warning: Configuración incompleta
 * Crítico: Sin configuración
 */
function evaluateFactor10_WhatsAppStatus(input: RiskCalculationInput): WhatsAppRiskFactor {
  const { envConfigured, scheduleConfigured } = input;
  
  let score = 100;
  let status: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
  let recommendation: string | undefined;
  
  if (envConfigured && scheduleConfigured) {
    score = 100;
    status = 'excellent';
  } else if (envConfigured && !scheduleConfigured) {
    score = 60;
    status = 'warning';
    recommendation = 'Configurar horarios en Settings → WhatsApp';
  } else if (!envConfigured && scheduleConfigured) {
    score = 40;
    status = 'warning';
    recommendation = 'Configurar .env en Settings → WhatsApp';
  } else {
    score = 10;
    status = 'danger';
    recommendation = '¡CRÍTICO! Configurar .env y horarios en Settings';
  }
  
  return {
    id: 'whatsapp_status',
    name: 'Estado WhatsApp',
    description: 'Configuración de sistema',
    weight: FACTOR_WEIGHTS.WHATSAPP_STATUS,
    currentValue: envConfigured && scheduleConfigured ? 'Completo' : 'Incompleto',
    optimalRange: { optimal: 'Completo' },
    score,
    status,
    recommendation
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// CÁLCULOS DE SCORE Y NIVEL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Calcula el score total como promedio ponderado
 */
function calculateWeightedScore(factors: WhatsAppRiskFactor[]): number {
  let totalScore = 0;
  let totalWeight = 0;
  
  factors.forEach(factor => {
    totalScore += factor.score * factor.weight;
    totalWeight += factor.weight;
  });
  
  return totalWeight > 0 ? totalScore / totalWeight : 0;
}

/**
 * Determina el nivel de riesgo basado en el score
 */
function determineRiskLevel(score: number): 'low' | 'medium' | 'high' | 'critical' {
  if (score >= 80) return 'low';
  if (score >= 60) return 'medium';
  if (score >= 40) return 'high';
  return 'critical';
}

/**
 * Calcula la probabilidad de flag basada en score y factores críticos
 */
function calculateFlagProbability(score: number, factors: WhatsAppRiskFactor[]): number {
  // Probabilidad base según score
  let baseProbability = 0;
  if (score >= 80) baseProbability = 7.5; // 5-10%
  else if (score >= 60) baseProbability = 23.5; // 15-32%
  else if (score >= 40) baseProbability = 56; // 40-72%
  else baseProbability = 82.5; // 75-90%
  
  // Penalización adicional por factores críticos
  const linkFactor = factors.find(f => f.id === 'links');
  const daysFactor = factors.find(f => f.id === 'sending_days');
  
  if (linkFactor && linkFactor.score < 50) {
    baseProbability += 10; // +10% si hay múltiples enlaces
  }
  
  if (daysFactor && daysFactor.score < 30) {
    baseProbability += 15; // +15% si incluye fines de semana
  }
  
  return Math.min(Math.round(baseProbability), 95);
}

// ═══════════════════════════════════════════════════════════════════════════
// GENERACIÓN DE RECOMENDACIONES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Genera recomendaciones priorizadas basadas en los factores
 */
function generateRecommendations(
  factors: WhatsAppRiskFactor[], 
  input: RiskCalculationInput
): string[] {
  const recommendations: Array<{ text: string; priority: number; impact: number }> = [];
  
  factors.forEach(factor => {
    if (factor.recommendation) {
      // Calcular prioridad e impacto
      const priority = factor.status === 'danger' ? 1 : factor.status === 'warning' ? 2 : 3;
      const impact = Math.round((100 - factor.score) * factor.weight);
      
      recommendations.push({
        text: factor.recommendation,
        priority,
        impact
      });
    }
  });
  
  // Ordenar por prioridad (danger primero) y luego por impacto
  recommendations.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    return b.impact - a.impact;
  });
  
  // Retornar solo texto, limitado a top 5
  return recommendations.slice(0, 5).map(r => r.text);
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILIDADES ADICIONALES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Detecta emojis en un string
 */
export function countEmojis(text: string): number {
  const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu;
  const matches = text.match(emojiRegex);
  return matches ? matches.length : 0;
}

/**
 * Detecta enlaces en un string
 */
export function countLinks(text: string): number {
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/g;
  const matches = text.match(urlRegex);
  return matches ? matches.length : 0;
}

/**
 * Detecta variables en formato [variable]
 */
export function countVariables(text: string): number {
  const varRegex = /\[([^\]]+)\]/g;
  const matches = text.match(varRegex);
  return matches ? matches.length : 0;
}

/**
 * Calcula mensajes por hora estimados
 */
export function estimateMessagesPerHour(totalContacts: number, rateLimit: number = 50): number {
  return Math.ceil(totalContacts / Math.ceil(totalContacts / rateLimit));
}

/**
 * Analiza días de envío y detecta fines de semana
 */
export function analyzeSendingDays(days: string[]): {
  hasWeekend: boolean;
  weekdayCount: number;
  weekendCount: number;
} {
  const weekend = ['SAT', 'SUN', 'SATURDAY', 'SUNDAY'];
  const normalizedDays = days.map(d => d.toUpperCase());
  
  const weekendCount = normalizedDays.filter(d => weekend.includes(d)).length;
  const weekdayCount = days.length - weekendCount;
  
  return {
    hasWeekend: weekendCount > 0,
    weekdayCount,
    weekendCount
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTACIONES
// ═══════════════════════════════════════════════════════════════════════════

export default {
  calculateRiskAssessment,
  countEmojis,
  countLinks,
  countVariables,
  estimateMessagesPerHour,
  analyzeSendingDays
};
